import {Choice, MultiChoice, useTranslationContext} from 'platform/components';
import {VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {always, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {Nullish, suffixTestId, useOnMount} from 'shared/web';

import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import {useChoiceFilter} from '../../hooks/useChoiceFilter';
import {useLazyChoiceFilterOptions} from '../../hooks/useLazyChoiceFilterOptions';
import {EagFilterProps} from '../../types/EagFilterProps';
import {getFilterFieldName} from '../../utils/getFilterFieldName';
import {getQuickFilterFromIntersection} from '../../utils/quickFilterValue';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';

export type ChoiceFilterProps = EagFilterProps & {
  columnKey: string;
  isMulti: boolean;
  quickFilters?: Array<{value: string; label: string}>;
};

type ChoiceOptions = ReturnType<typeof useLazyChoiceFilterOptions>;

type ChoiceControlRendererProps = {
  isMulti: boolean;
  values: string[] | string;
  options: ChoiceOptions['options'];
  onChange: (changedValues: string[] | string | number | null) => void;
  placeholder: string;
  isDisabled: boolean;
  isLoading: boolean;
  errorMessage: ChoiceOptions['errorMessage'];
  'data-testid'?: string;
};

/**
 * Renders the single- or multi-select control for ChoiceFilter. Both variants
 * share every prop except the rendered component and the selection value shape,
 * so the value transform is centralised here keyed on `isMulti` to keep the two
 * shapes from drifting apart.
 */
function ChoiceControlRenderer(props: ChoiceControlRendererProps) {
  const sharedProps = {
    options: props.options,
    onChange: props.onChange,
    placeholder: props.placeholder,
    isDisabled: props.isDisabled,
    isLoading: props.isLoading,
    errorMessage: props.errorMessage,
    menuInPortal: true,
    'data-testid': props['data-testid'],
  };

  return props.isMulti ? (
    <MultiChoice {...sharedProps} value={isArray(props.values) ? props.values : [props.values]} />
  ) : (
    <Choice {...sharedProps} value={isArray(props.values) ? props.values[0] : props.values} />
  );
}

export function ChoiceFilter(props: ChoiceFilterProps) {
  const t = useTranslationContext();

  const rawModel = props.value[FILTER_VALUE_KEY] as string[] | string | Nullish;

  const [values, setValues] = useChoiceFilter({
    model: rawModel,
    onChange: (newValue) => {
      // useChoiceFilter collapses an empty selection to `null`; restore an
      // empty-array shape so downstream consumers never see `{value: null}`.
      props.onChange({[FILTER_VALUE_KEY]: isNil(newValue) ? [] : newValue});
    },
  });

  const {loadOptions, isLoading, options, errorMessage} = useLazyChoiceFilterOptions({
    columnKey: props.columnKey,
  });

  const isQuickFilterValue = getQuickFilterFromIntersection(values);

  const onChange = (changedValues: string[] | string | number | null) => {
    const correctedValues = match<string[] | string | number | null, string[] | Nullish>(
      changedValues
    )
      .with(Pattern.array(Pattern.string), (v) => v)
      .with(Pattern.number, (v) => [String(v)])
      .with(Pattern.string, (v) => [v])
      .otherwise(always(null));

    setValues(correctedValues);
  };

  const isControlDisabled = props.isDisabled || isLoading;

  useOnMount(() => {
    void loadOptions();
  });

  return (
    // spacing keeps the quick-filter chip strip from touching the main control —
    // without it the two rows render with no vertical gap (T20-134467).
    <VStack spacing={2}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={isQuickFilterValue}
        defaultValue={null}
        setFilterValue={setValues}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('choice-filter-quickFilters', props)}
      />
      <ChoiceControlRenderer
        isMulti={props.isMulti}
        values={values}
        options={options}
        onChange={onChange}
        placeholder={t('page.datagrid.filter.choicePlaceholder', {
          field: getFilterFieldName(props.column),
        })}
        isDisabled={isControlDisabled}
        isLoading={isLoading}
        errorMessage={errorMessage}
        data-testid={suffixTestId('choice-filter', props)}
      />
    </VStack>
  );
}
